import { logger } from "./logger";

/**
 * Wrap a print-handler call. Print handlers (in @shared/functions/print) do
 * fragile math + string building on loan/route/config objects, and a thrown
 * error there aborts the surrounding transactional flow. This:
 *   1. logs the FULL payload sent to the printer (so a print failure can be
 *      diagnosed from the audit log), and
 *   2. never rethrows — a printing failure must not break saving/navigation.
 *
 * Usage: safePrint("print.entrega", printEntregaHandler, loan, route, configs)
 */
export function safePrint(action, printFn, ...args) {
  try {
    const result = printFn(...args);
    // Log BOTH the full input payload and the produced receipt string, so a
    // "wrong data / wrong calculation" receipt can be fully reconstructed from
    // the log (inputs) and compared against what actually printed (receipt).
    logger.info(action, "Print produced", {
      context: {
        data: args,
        receipt: typeof result === "string" ? result : undefined,
      },
    });

    // High-signal, low-volume: when a PAYMENT receipt actually prints "Atrasos
    // Pendiente", emit a structured breakdown of every field that drives the
    // atrasos = pending*wPayment - paymentAmount - incomplete - previousAmount
    // calc, PLUS the loan schedule fields (given/start/next_date, elapsed) needed
    // to judge whether `pending` itself is correct. Makes a "wrong atrasos" report
    // diagnosable from the log alone — no backend forensics.
    if (action === "print.receipt" && typeof result === "string" && /Atrasos Pendiente/i.test(result)) {
      const p = (args && args[0]) || {};
      logger.warning("receipt.atrasos_shown", "Receipt printed with atrasos pendiente", {
        entity: "payments",
        entityId: p.pUniqueId,
        context: {
          money_id: p.money_id,
          name: p.name,
          // atrasos formula inputs
          pending: p.pending,
          wPayment: p.wPayment,
          paymentAmount: p.paymentAmount,
          paymentMora: p.paymentMora,
          incomplete: p.incomplete,
          previousAmount: p.previousAmount,
          balance: p.balance,
          completed: p.completed,
          npayments: p.npayments,
          mainApp: p.mainApp,
          // schedule fields — the ones we had to reverse-engineer for loan 576757
          given_date: p.given_date,
          start_date: p.start_date,
          next_date: p.next_date,
          elapsed: p.elapsed,
        },
      });
    }

    return result;
  } catch (err) {
    logger.error(`${action}_failed`, "Print handler failed", {
      context: { error: String(err?.message || err), stack: err?.stack, data: args },
    });
    return undefined;
  }
}
